import type { SendOtpParams, VerifyOtpParams, VerifyOtpResponse } from '@/api/schema'
import { Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Motion } from '@/app/components/Motion/Motion'
import { Terms } from '@/app/components/Terms/Terms'
import { Text } from '@/app/components/Text/Text'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import ArrowLeftSvg from '@/app/icons/arrow-left.svg?react'
import { useSession } from '@/app/state/session'
import { api } from '@/app/utils/api'
import { parseError } from '@/app/utils/error'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import styles from './index.module.scss'

enum Step {
  Email,
  Otp,
}

export const OfferLogin: FunctionalComponent<{
  onBack: () => void
  onSuccess: () => void
}> = props => {
  const fetchSession = useSession(s => s.fetch)

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState(Step.Email)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const [success, setSuccess] = useState(false)

  const submitEmail = async () => {
    try {
      setLoading(true)
      setErrors([])

      await api.post(
        '/user/send-otp',
        {
          email,
        } satisfies SendOtpParams,
      )

      metrikaGoal('login_submit_email')
      setStep(Step.Otp)
    }
    catch (e) {
      console.error(e)
      setErrors(parseError(e))
    }
    finally {
      setLoading(false)
    }
  }

  const submitCode = async () => {
    try {
      setLoading(true)
      setErrors([])

      await api.post<VerifyOtpResponse>(
        '/user/email/verify-otp',
        {
          email,
          code,
        } satisfies VerifyOtpParams,
      )

      metrikaGoal('login_submit_code')
      await fetchSession()
      setSuccess(true)

      setTimeout(() => {
        props.onSuccess()
      }, 1500)
    }
    catch (e) {
      console.error(e)
      setErrors(parseError(e))
      setLoading(false)
    }
  }

  useEffect(() => {
    metrikaGoal('login')
  }, [])

  return (
    <form
      class={styles.root}
      onSubmit={e => {
        e.preventDefault()

        if (step === Step.Email) {
          submitEmail()
        }
        else {
          submitCode()
        }
      }}
    >
      {step === Step.Otp
        ? (
          <Motion key='otp'>
            <Row gap={15}>
              <div class={styles.header}>
                <button
                  type='button'
                  class={styles.back}
                  onClick={() => {
                    setErrors([])
                    setStep(Step.Email)
                  }}
                >
                  <ArrowLeftSvg strokeWidth={1.8} />
                </button>

                <Text
                  fontSize={20}
                  fontSizeMobile={18}
                  fontWeight={600}
                  textAlign='center'
                >
                  Проверь почту
                </Text>
              </div>

              {success
                ? (
                  <Motion>
                    <Text fontSize={16}>
                      Код подтверждён. Сейчас откроем страницу оплаты.
                    </Text>
                  </Motion>
                )
                : (
                  <Text fontSize={16}>
                    Код отправлен на <b>{email || 'твой email'}</b>. Проверь письмо и введи его ниже.
                  </Text>
                )}

              <Input
                type='tel'
                placeholder='Код из письма'
                disabled={loading}
                onInput={setCode}
                onChange={setCode}
                value={code}
                maxLength={4}
                minLength={4}
              />

              <Lib.Errors errors={errors} />

              <UI.Button
                block
                size='l'
                type='primary'
                htmlType='submit'
                disabled={loading}
              >
                Продолжить
              </UI.Button>

              <UI.Hint>
                <Terms />
              </UI.Hint>
            </Row>
          </Motion>
        )
        : (
          <Motion key='email'>
            <Row gap={15}>
              <div class={styles.header}>
                <button
                  type='button'
                  class={styles.back}
                  onClick={props.onBack}
                >
                  <ArrowLeftSvg strokeWidth={1.5} />
                </button>

                <Text
                  fontSize={20}
                  fontSizeMobile={18}
                  fontWeight={600}
                  textAlign='center'
                >
                  Куда сохранить доступ?
                </Text>
              </div>

              <Text fontSize={16}>
                Введи email — к нему будут привязаны оплата, этот чат и доступ ко всем твоим разборам.
              </Text>

              <Input
                type='email'
                placeholder='Введи email'
                disabled={loading}
                onInput={setEmail}
                onChange={setEmail}
                value={email}
              />

              <Lib.Errors errors={errors} />

              <UI.Button
                block
                size='l'
                type='primary'
                htmlType='submit'
                disabled={loading}
              >
                Продолжить
              </UI.Button>

              <UI.Hint>
                <Terms />
              </UI.Hint>
            </Row>
          </Motion>
        )}
    </form>
  )
}
